import { Button } from "@/components/ui/button";
import { useNotifications, type Notification } from "@/hooks/useNotifications";
import {
  AlertTriangle,
  Bell,
  Calendar,
  CheckCheck,
  FileText,
  Info,
  MessageSquare,
  RefreshCw,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

// ─── Notification Type Icons ────────────────────────────────────────────────

const typeIcons: Record<string, React.ReactNode> = {
  SESSAO_LEMBRETE: <Calendar className="h-4 w-4 text-blue-500" />,
  SESSAO_CONFIRMADA: <Calendar className="h-4 w-4 text-green-500" />,
  SESSAO_CANCELADA: <Calendar className="h-4 w-4 text-red-500" />,
  QUESTIONARIO_LINK: <FileText className="h-4 w-4 text-purple-500" />,
  DOCUMENTO_DISPONIVEL: <FileText className="h-4 w-4 text-teal-500" />,
  PRAZO_PROXIMO: <AlertTriangle className="h-4 w-4 text-amber-500" />,
  NR1_CAMPANHA: <MessageSquare className="h-4 w-4 text-indigo-500" />,
  RELATORIO_PRONTO: <FileText className="h-4 w-4 text-emerald-500" />,
  SISTEMA: <Info className="h-4 w-4 text-slate-500" />,
};

// ─── Helper Functions ───────────────────────────────────────────────────────

function getNotificationIcon(type: string): React.ReactNode {
  return typeIcons[type] || <Bell className="h-4 w-4 text-slate-500" />;
}

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "agora";
    if (diffMins < 60) return `há ${diffMins} min`;
    if (diffHours < 24) return `há ${diffHours}h`;
    if (diffDays < 7) return `há ${diffDays}d`;
    return date.toLocaleDateString("pt-BR");
  } catch {
    return "agora";
  }
}

// ─── Notification Item Component ────────────────────────────────────────────

interface NotificationItemProps {
  notification: Notification;
  onRead: (id: string) => void;
}

function NotificationItem({ notification, onRead }: NotificationItemProps) {
  const isUnread = !notification.readAt;

  const handleClick = () => {
    if (isUnread) {
      onRead(notification.id);
    }
  };

  return (
    <button
      type="button"
      className={`flex w-full items-start gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors text-left ${
        isUnread ? "bg-blue-50/50" : ""
      }`}
      onClick={handleClick}
    >
      <div className="flex-shrink-0 mt-0.5">
        {getNotificationIcon(notification.type)}
      </div>
      <div className="flex-1 min-w-0">
        {notification.title && (
          <p
            className={`text-sm ${isUnread ? "font-semibold" : "font-medium"} text-slate-900 truncate`}
          >
            {notification.title}
          </p>
        )}
        <p className="text-sm text-slate-600 line-clamp-2">
          {notification.message}
        </p>
        <p className="text-xs text-slate-400 mt-1">
          {formatDate(notification.createdAt)}
        </p>
      </div>
      {isUnread && (
        <div className="flex-shrink-0">
          <div className="h-2 w-2 rounded-full bg-blue-500" />
        </div>
      )}
    </button>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function NotificationBell() {
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refresh,
  } = useNotifications(30000); // Poll every 30 seconds

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        className="relative h-11 w-11 rounded-full hover:bg-slate-100"
        aria-label={`Notificações${unreadCount > 0 ? ` (${unreadCount} não lidas)` : ""}`}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Bell className="h-5 w-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-[400px] bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden flex flex-col z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <span className="text-base font-semibold text-slate-900">
              Notificações
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => refresh()}
                disabled={loading}
              >
                <RefreshCw
                  className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                />
              </Button>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => markAllAsRead()}
                >
                  <CheckCheck className="h-3.5 w-3.5 mr-1" />
                  Ler todas
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-slate-500">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhuma notificação</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onRead={markAsRead}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-2 border-t border-slate-200">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-slate-500 hover:text-slate-700"
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = "/notificacoes";
                }}
              >
                Ver todas as notificações
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
